import React from 'react'
import { useList } from '@refinedev/core'
import { Table, Space, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { TableSurface, EmptyHint, Pill, TONES, type Tone } from '../../theme/listkit'
import { BadgeCheck, XCircle } from '../../components/icons'
import { monthLabel } from './botm'
import { useJaguarOwnedSwitches } from '../jaguar_owned_controls/useJaguarOwnedSwitches'

/** Row shape of the `business_spotlight_eligibility` view (admin-only read —
 * `backend/supabase/migrations/20260923120200_jaguar_owned_spotlight.sql`). */
export interface EligibilityRow {
  business_id: string
  name: string | null
  owner_user_id: string | null
  status: string | null
  days_live: number | null
  live_60_days: boolean | null
  independent_vouches: number | null
  has_3_vouches: boolean | null
  complete_listing: boolean | null
  has_perk: boolean | null
  strikes: number | null
  no_strikes: boolean | null
  not_distributor: boolean | null
  fresh: boolean | null
  last_win: string | null
  no_recent_win: boolean | null
  eligible: boolean | null
}

/** Check-or-cross cell: an icon plus the value, where a value is meaningful. */
function CheckCell({ pass, value }: { pass: boolean | null; value?: React.ReactNode }) {
  const ok = pass === true
  return (
    <Space size={6}>
      {ok ? <BadgeCheck size={15} color={TONES.green.fg} /> : <XCircle size={15} color={TONES.red.fg} />}
      {value != null ? (
        <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12.5, color: brand.sub }}>{value}</span>
      ) : null}
    </Space>
  )
}

/** `1 day` / `N days`. */
function dayLabel(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`
}

const ELIGIBILITY_CHECKS: (keyof EligibilityRow)[] = [
  'live_60_days',
  'has_3_vouches',
  'complete_listing',
  'has_perk',
  'no_strikes',
  'not_distributor',
  'fresh',
  'no_recent_win',
]

/**
 * Result pill: Eligible, Paused (the listing itself is paused — takes
 * precedence over Cooldown/Not yet, since a paused business isn't "almost
 * there", it's off), Cooldown (only the win-cooldown check is failing), or
 * Not yet. The view's `eligible` column already requires `status = 'approved'`
 * (`business_spotlight_eligibility` also admits `'paused'` listings so the
 * committee can see them), so a paused row is never `eligible` — but without
 * this pill it would fall through to a misleading Cooldown/Not yet instead of
 * naming the real reason.
 */
function resultPill(row: EligibilityRow): { tone: Tone; label: string } {
  if (row.eligible) return { tone: 'green', label: 'Eligible' }
  if (row.status === 'paused') return { tone: 'neutral', label: 'Paused' }
  const otherChecksPass =
    row.status === 'approved' &&
    ELIGIBILITY_CHECKS.filter((k) => k !== 'no_recent_win').every((k) => row[k] === true)
  if (otherChecksPass && row.no_recent_win === false) return { tone: 'red', label: 'Cooldown' }
  return { tone: 'gold', label: 'Not yet' }
}

/**
 * Eligibility tab — the automatic checks every approved/paused listing is
 * scored against, read straight from the `business_spotlight_eligibility` view
 * (admins read every row; the SQL, not this component, decides who's eligible).
 * Eligible businesses sort first so the committee sees who's ready without
 * scanning past everyone who isn't.
 */
export function EligibilityTab() {
  const { result, query } = useList<EligibilityRow>({
    resource: 'business_spotlight_eligibility',
    pagination: { pageSize: 500 },
  })
  // The perks switch waives the perk requirement for Business of the Month
  // (Task C1's migration, `jaguar_owned_perks_waived()`) — the "Member perk"
  // column reflects that waiver instead of showing a check/cross that would
  // no longer mean what it says. `has_perk` itself is untouched (the raw
  // fact); only how this column PRESENTS it changes.
  //
  // `jaguar_owned_perks_waived()` treats a MISSING row as NOT waived (the
  // requirement still applies) — only an explicit, disabled row waives it.
  // `waived` mirrors that exactly: it's false whenever `perksRow` is
  // undefined, never defaulting a missing row to "off". It's also false
  // while the switches haven't loaded yet (or failed to), so the column
  // shows its ordinary check/cross rather than guessing.
  //
  // The vouches switch (Task P2C / migration `20260923121300_jaguar_owned_
  // trust_switches.sql`) waives the three-vouch requirement the same way —
  // `jaguar_owned_vouches_waived()` also treats a missing row as NOT waived.
  // Unlike perks, the view itself already folds the waiver into `has_3_
  // vouches` (there's no separate raw-fact column for the vouches CHECK the
  // way `has_perk` is for perks — the raw count stays in `independent_
  // vouches`), so `r.has_3_vouches` reads `true` once vouches are off
  // regardless of the real count. That's still the right server behaviour
  // (it's what `eligible` and the scheduling guard consult), but showing a
  // plain green check here would misleadingly imply three vouches were
  // actually counted, so this column presents the same "Not required" pill
  // the perk column does, keyed off the switch rather than the (now
  // always-true) `has_3_vouches` value.
  const { byKey: switches, isLoading: switchesLoading, isError: switchesError } = useJaguarOwnedSwitches()
  const perksRow = switches.get('jaguar_owned_perks_enabled')
  const perksWaived = !switchesLoading && !switchesError && !!perksRow && !perksRow.enabled
  const vouchesRow = switches.get('jaguar_owned_vouches_enabled')
  const vouchesWaived = !switchesLoading && !switchesError && !!vouchesRow && !vouchesRow.enabled

  const rows = React.useMemo(() => {
    const data = result?.data ?? []
    return [...data].sort((a, b) => {
      if (a.eligible === b.eligible) return (a.name ?? '').localeCompare(b.name ?? '')
      return a.eligible ? -1 : 1
    })
  }, [result])

  if (!query.isLoading && rows.length === 0) {
    return <EmptyHint>No businesses to check yet.</EmptyHint>
  }

  return (
    <div>
      <TableSurface>
        <Table<EligibilityRow>
          dataSource={rows}
          loading={query.isLoading}
          rowKey="business_id"
          pagination={false}
          scroll={{ x: true }}
        >
          <Table.Column<EligibilityRow>
            dataIndex="name"
            title="Business"
            render={(name: string | null, r) => (
              <div>
                <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 13.5, color: brand.ink }}>
                  {name ?? '—'}
                </div>
                {r.status === 'paused' ? (
                  <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub }}>
                    Paused
                  </div>
                ) : null}
              </div>
            )}
          />
          <Table.Column<EligibilityRow>
            dataIndex="live_60_days"
            title="Live 60+ days"
            render={(_: unknown, r) => <CheckCell pass={r.live_60_days} value={dayLabel(r.days_live ?? 0)} />}
          />
          <Table.Column<EligibilityRow>
            dataIndex="has_3_vouches"
            title={
              vouchesWaived ? (
                <Tooltip title="Vouches are off, so Business of the Month doesn't require three.">
                  <span>3+ independent vouches</span>
                </Tooltip>
              ) : (
                '3+ independent vouches'
              )
            }
            render={(_: unknown, r) =>
              vouchesWaived ? (
                <Pill tone="neutral">Not required</Pill>
              ) : (
                <CheckCell pass={r.has_3_vouches} value={r.independent_vouches ?? 0} />
              )
            }
          />
          <Table.Column<EligibilityRow>
            dataIndex="complete_listing"
            title="Complete listing"
            render={(_: unknown, r) => <CheckCell pass={r.complete_listing} />}
          />
          <Table.Column<EligibilityRow>
            dataIndex="has_perk"
            title={
              perksWaived ? (
                <Tooltip title="Perks are off, so Business of the Month doesn't require one.">
                  <span>Member perk</span>
                </Tooltip>
              ) : (
                'Member perk'
              )
            }
            render={(_: unknown, r) =>
              perksWaived ? <Pill tone="neutral">Not required</Pill> : <CheckCell pass={r.has_perk} />
            }
          />
          <Table.Column<EligibilityRow>
            dataIndex="no_strikes"
            title="No strikes"
            render={(_: unknown, r) => <CheckCell pass={r.no_strikes} value={r.strikes ?? 0} />}
          />
          <Table.Column<EligibilityRow>
            dataIndex="not_distributor"
            title="Not a distributor"
            render={(_: unknown, r) => <CheckCell pass={r.not_distributor} />}
          />
          <Table.Column<EligibilityRow>
            dataIndex="fresh"
            title="Confirmed in 6 months"
            render={(_: unknown, r) => <CheckCell pass={r.fresh} />}
          />
          <Table.Column<EligibilityRow>
            dataIndex="no_recent_win"
            title="No win in 24 months"
            render={(_: unknown, r) => (
              <CheckCell pass={r.no_recent_win} value={r.last_win ? monthLabel(r.last_win) : undefined} />
            )}
          />
          <Table.Column<EligibilityRow>
            dataIndex="eligible"
            title="Result"
            render={(_: unknown, r) => {
              const { tone, label } = resultPill(r)
              return <Pill tone={tone}>{label}</Pill>
            }}
          />
        </Table>
      </TableSurface>
      <div
        style={{
          fontFamily: brand.fSans,
          fontWeight: 500,
          fontSize: 11.5,
          color: brand.sub,
          marginTop: 10,
        }}
      >
        {"Checks update automatically. Vouches from family or employees don't count. Strikes are reports admins acted on in the last 24 months."}
      </div>
    </div>
  )
}
